import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import apiService from '../api/apiService'
import Loading from '../components/ui/Loading'

// Google OAuth Client ID
const GOOGLE_CLIENT_ID =
  '314893220530-m5r12h0n9r349dshacbilr7beelrt687.apps.googleusercontent.com'

// Google OAuth scopes
const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/userinfo.email',
].join(' ')

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessToken, setAccessToken] = useState(null)

  // Function to store user data in the database if they don't already exist
  const storeUserInDatabase = useCallback(async (userData) => {
    try {
      const userPayload = {
        first_name: userData.given_name,
        last_name: userData.family_name,
        email: userData.email,
        image_url: userData.picture,
        last_login: new Date().toISOString(),
      }

      await apiService.createOrUpdateUser(userPayload)
    } catch (error) {
      console.error('Error storing user in database:', error)
    }
  }, [])

  // Handle Google credential response
  const handleGoogleCredentialResponse = useCallback(
    async (response) => {
      if (response.credential) {
        try {
          // Decode the JWT token to get user info
          const base64Url = response.credential.split('.')[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
              })
              .join('')
          )

          const decodedToken = JSON.parse(jsonPayload)

          // Create user object
          const googleUser = {
            id: decodedToken.sub,
            email: decodedToken.email,
            name: decodedToken.name,
            picture: decodedToken.picture,
            given_name: decodedToken.given_name,
            family_name: decodedToken.family_name,
          }

          // Set user in state and localStorage
          setUser(googleUser)
          localStorage.setItem('user', JSON.stringify(googleUser))

          // Store user data in the database
          storeUserInDatabase(googleUser)
        } catch (error) {
          console.error('Error processing Google credential:', error)
        }
      }
    },
    [storeUserInDatabase]
  )

  // Let components know Google is ready with a custom event
  const dispatchGoogleLoadedEvent = () => {
    try {
      // Create a custom event with timestamp to avoid duplicate processing
      const googleEvent = new CustomEvent('google-loaded', {
        detail: {
          timestamp: Date.now(),
          initialized: true,
        },
      })
      window.dispatchEvent(googleEvent)
    } catch (e) {
      // Fallback for older browsers
      window.dispatchEvent(new Event('google-loaded'))
    }
  }

  // Initialize Google Sign-In
  const initializeGoogleSignIn = useCallback(() => {
    // Prevent multiple initializations
    if (window._googleInitialized) {
      // If already initialized, just dispatch the event
      dispatchGoogleLoadedEvent()
      return
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        // Initialize Google Sign-In
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: 'popup',
          locale: 'en_US', // Force English language
        })

        // Mark as initialized
        window._googleInitialized = true

        // Let components know Google is ready
        dispatchGoogleLoadedEvent()
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error)
      }
    }
  }, [handleGoogleCredentialResponse])

  // Check for stored user on initial load
  useEffect(() => {
    // Check for stored user and token
    const storedUser = localStorage.getItem('user')
    const storedToken = localStorage.getItem('accessToken')

    if (storedToken) {
      setAccessToken(storedToken)
    }

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setUser(parsedUser)
      } catch (error) {
        // If there's an error parsing the stored user, clear it
        localStorage.removeItem('user')
      }
    }

    setLoading(false)

    // Load Google API
    const loadGoogleAPI = () => {
      // Track initialization to prevent duplication
      if (window._googleInitialized) return

      // Check if script already exists in document
      if (
        document.querySelector('script[src*="accounts.google.com/gsi/client"]')
      ) {
        // Script exists, check if Google object is available
        if (
          window.google &&
          window.google.accounts &&
          window.google.accounts.id
        ) {
          initializeGoogleSignIn()
        }
        return
      }

      // Create and add script if needed
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client?hl=en'
      script.async = true
      script.defer = true
      script.onload = () => {
        initializeGoogleSignIn()
      }
      document.body.appendChild(script)
    }

    // Check if Google API is already loaded
    if (!window.google || !window.google.accounts) {
      loadGoogleAPI()
    } else {
      initializeGoogleSignIn()
      // If Google is already loaded, dispatch the event anyway to notify components
      window.dispatchEvent(new Event('google-loaded'))
    }
  }, [initializeGoogleSignIn])

  // Sign in with Google
  const signInWithGoogle = useCallback(() => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.prompt()
    } else if (
      window.google &&
      window.google.accounts &&
      window.google.accounts.oauth2 &&
      GOOGLE_CLIENT_ID
    ) {
      try {
        // Use Google OAuth 2.0 for authorization with Keep API scope
        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPES,
          callback: async (response) => {
            if (response.code) {
              try {
                // In a real app, you would send this code to your backend
                // Your backend would exchange it for tokens using client secret

                // For now, we'll use the token we get from Google Sign-In
                // This is just a placeholder - in a real app, you need proper token exchange
                const tokenResponse = await fetch(
                  'https://oauth2.googleapis.com/token',
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({
                      code: response.code,
                      client_id: GOOGLE_CLIENT_ID,
                      // In a real app, you would include client_secret, but it should be kept on server
                      redirect_uri: window.location.origin,
                      grant_type: 'authorization_code',
                    }),
                  }
                )

                const tokenData = await tokenResponse.json()
                const token = tokenData.access_token

                // Store token separately
                setAccessToken(token)
                localStorage.setItem('accessToken', token)

                // Fetch user info
                const userInfoResponse = await fetch(
                  'https://www.googleapis.com/oauth2/v3/userinfo',
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                )

                const userData = await userInfoResponse.json()

                // Create user object without token (to avoid token in localStorage)
                const googleUser = {
                  id: userData.sub,
                  email: userData.email,
                  name: userData.name,
                  picture: userData.picture,
                  given_name: userData.given_name,
                  family_name: userData.family_name,
                }

                setUser(googleUser)
                localStorage.setItem('user', JSON.stringify(googleUser))

                // Store user data in the database
                storeUserInDatabase(googleUser)
              } catch (error) {
                console.error('Token exchange error:', error)
              }
            }
          },
          error_callback: (error) => {
            console.error('OAuth error:', error)
          },
        })

        // Start the OAuth flow
        client.requestCode()
      } catch (error) {
        console.error('Google sign-in error:', error)
      }
    }
  }, [storeUserInDatabase])

  // Handle OAuth redirect response
  useEffect(() => {
    // Check if we have a hash fragment in the URL (indicates OAuth redirect)
    const hash = window.location.hash
    if (hash && hash.includes('access_token')) {
      // Parse the access token from the URL
      const params = new URLSearchParams(hash.substring(1))
      const accessToken = params.get('access_token')

      if (accessToken) {
        // Fetch user info using the access token
        fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => response.json())
          .then((data) => {
            // Create user object from Google data
            const googleUser = {
              id: data.sub,
              email: data.email,
              name: data.name,
              picture: data.picture,
              given_name: data.given_name,
              family_name: data.family_name,
            }

            // Set user in state and localStorage
            setUser(googleUser)
            localStorage.setItem('user', JSON.stringify(googleUser))

            // Store user data in the database
            storeUserInDatabase(googleUser)

            // Clear the hash fragment from the URL
            window.history.replaceState(null, '', window.location.pathname)
          })
          .catch((error) => {
            console.error('Error fetching user info:', error)
          })
      }
    }
  }, [storeUserInDatabase])

  // Sign out
  const signOut = () => {
    setUser(null)
    setAccessToken(null)
    localStorage.removeItem('user')
    localStorage.removeItem('accessToken')

    // Navigate to home page
    window.location.href = '/'
  }

  const value = {
    user,
    accessToken,
    signInWithGoogle,
    signOut,
    storeUserInDatabase,
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <Loading />}
    </AuthContext.Provider>
  )
}
